import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './create-packit.css';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useWebSocket } from './WebSocketContext';

const CreatePackitForm = ({ onBack, initialFormData, initialDestinations, selectedNotesSummary = [] }) => {
  const defaultFormState = {
    tripName: '',
    description: '',
    startDate: null,
    endDate: null,
    isFlexibleDate: false,
    category: '',
    tripType: '',
    includesKids: false,
    includesPets: false,
    receiveAlerts: false,
    alertFrequency: '',
    packingNeeds: [],
    interests: [],
    selectedColors: [], // Added for color selection
    checkpoints: [],
    primaryTravelMode: '',
    secondaryTravelModes: []
  };

  const prepareInitialFormState = (data) => {
    if (!data) {
      return defaultFormState;
    }
    const packingNeedsArray = Array.isArray(data.packingNeeds)
      ? data.packingNeeds.map((item) => item.toLowerCase())
      : [];
    const interestsArray = Array.isArray(data.interests) ? data.interests : [];
    const selectedColorsArray = Array.isArray(data.selectedColors)
      ? data.selectedColors.map((color) => color.toLowerCase())
      : [];

    return {
      ...defaultFormState,
      ...data,
      startDate: data.startDate ? new Date(data.startDate) : null,
      endDate: data.endDate ? new Date(data.endDate) : null,
      packingNeeds: Array.from(new Set(packingNeedsArray)),
      interests: Array.from(new Set(interestsArray)),
      selectedColors: Array.from(new Set(selectedColorsArray)),
      primaryTravelMode: data.primaryTravelMode || '',
      secondaryTravelModes: Array.isArray(data.secondaryTravelModes) ? data.secondaryTravelModes : [],
    };
  };

  const [formData, setFormData] = useState(() => prepareInitialFormState(initialFormData));
  
  const navigate = useNavigate();
  const [colorCategories, setColorCategories] = useState({});
  const [allInterests, setAllInterests] = useState([
    'Attractions',
    'Food',
    'Art'
  ]); // Predefined and user-added bubbles
  const [customInterest, setCustomInterest] = useState('');
  const [showAddBubbleModal, setShowAddBubbleModal] = useState(false);
  // Instead of just { id: Date.now(), name: '' }, include days
  const [destinations, setDestinations] = useState(() => {
    if (Array.isArray(initialDestinations) && initialDestinations.length > 0) {
      return initialDestinations.map((destination, index) => ({
        id: destination.id || `initial-destination-${index}-${Date.now()}`,
        name: destination.name || '',
        minimumNumberOfDesiredDaysAtLocation: Number(destination.minimumNumberOfDesiredDaysAtLocation) || 1,
        isStart: Boolean(destination.isStart),
        isEnd: Boolean(destination.isEnd),
      }));
    }
    return [
      { id: Date.now(), name: '', minimumNumberOfDesiredDaysAtLocation: 1, isStart: false, isEnd: false }
    ];
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  const { connectionId } = useWebSocket(); 

  // User plan and trial status for validation
  const [userPlan, setUserPlan] = useState('');
  const [packitTrialComplete, setPackitTrialComplete] = useState(false);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoadingUserInfo(true);
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const user = getUserFromToken(token);
        
        if (!user) {
          alert('Failed to retrieve user from JWT token.');
          setIsLoadingUserInfo(false);
          return;
        }

        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`
        };

        const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
        const response = await axios.get(url, { headers });

        const userData = response.data[0];
        
        // Set categorization
        const data = userData.categorization || {};
        setColorCategories(data);
        
        // Set plan and trial status for validation
        setUserPlan(userData.plan || '');
        setPackitTrialComplete(Boolean(userData.packitTrialComplete));
        
        // Check if user can create packits
        // Only Globe Trotter can create packits (Basic has 1 trial, Adventurer cannot)
        if (userData.plan === 'Basic' && userData.packitTrialComplete) {
          // Basic user with trial complete - redirect to pack-it page
          alert('You have reached your Pack-It trial limit. Please upgrade to Globe Trotter to create more Pack-Its.');
          navigate('/pack-it');
          return;
        } else if (userData.plan === 'Adventurer') {
          // Adventurer plan cannot create packits - only Globe Trotter can
          alert('Pack-Its are only available for Globe Trotter plan users. Please upgrade to Globe Trotter to create Pack-Its.');
          navigate('/pack-it');
          return;
        }
      } catch (error) {
        console.error('Error fetching user information:', error);
      } finally {
        setIsLoadingUserInfo(false);
      }
    };
    fetchUserInfo();
  }, [navigate, apiUrl]);

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  const handleColorToggle = (color) => {
    setFormData((prev) => ({
      ...prev,
      selectedColors: prev.selectedColors.includes(color)
        ? prev.selectedColors.filter((c) => c !== color)
        : [...prev.selectedColors, color],
    }));
  };

  const handleUpdateDestination = (id, key, value) => {
    setDestinations((currentDestinations) =>
      currentDestinations.map((destination) => {
        if (destination.id !== id) return destination;

        const updated = { ...destination, [key]: value };

        // If user selects 'isStart' or 'isEnd' = true -> set days to 0
        if ((key === 'isStart' || key === 'isEnd') && value === true) {
          updated.minimumNumberOfDesiredDaysAtLocation = 0;
        }

        return updated;
      })
    );
  };

  const handleAddDestination = () => {
    //setDestinations([...destinations, { id: Date.now(), name: '', days: 1 }]);
    setDestinations([
      ...destinations,
      {
        id: Date.now(),
        name: '',
        minimumNumberOfDesiredDaysAtLocation: 0,
        isStart: false,
        isEnd: false
      }
    ]);
  };
     

  const handlePackingNeedsToggle = (need) => {
    setFormData((prev) => ({
      ...prev,
      packingNeeds: prev.packingNeeds.includes(need)
        ? prev.packingNeeds.filter((n) => n !== need)
        : [...prev.packingNeeds, need]
    }));
  };

  const handleRemoveDestination = (id) => {
    setDestinations(destinations.filter((destination) => destination.id !== id));
  };

  const handleInterestToggle = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  // Travel mode handlers
  const handlePrimaryTravelModeChange = (mode) => {
    setFormData((prev) => ({
      ...prev,
      primaryTravelMode: mode
    }));
  };

  const handleSecondaryTravelModeToggle = (mode) => {
    setFormData((prev) => {
      const currentModes = Array.isArray(prev.secondaryTravelModes) ? prev.secondaryTravelModes : [];
      return {
        ...prev,
        secondaryTravelModes: currentModes.includes(mode)
          ? currentModes.filter((m) => m !== mode)
          : [...currentModes, mode]
      };
    });
  };

  const handleAddCustomInterest = () => {
    if (customInterest.trim() && !allInterests.includes(customInterest)) {
      setAllInterests((prev) => [...prev, customInterest]);
      setFormData((prev) => ({
        ...prev,
        interests: [...prev.interests, customInterest]
      }));
      setCustomInterest('');
      setShowAddBubbleModal(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate user can create packits
    // Only Globe Trotter can create packits (Basic has 1 trial, Adventurer cannot)
    if (userPlan === 'Basic' && packitTrialComplete) {
      alert('You have reached your Pack-It trial limit. Please upgrade to Globe Trotter to create more Pack-Its.');
      navigate('/pack-it');
      return;
    } else if (userPlan === 'Adventurer') {
      alert('Pack-Its are only available for Globe Trotter plan users. Please upgrade to Globe Trotter to create Pack-Its.');
      navigate('/pack-it');
      return;
    }
  
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();

    if (!token) {
      alert('You need to be logged in to create a Pack-It.');
      return;
    }

    // Calculate trip duration in days
    let tripDuration = 0;
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      tripDuration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
    }

    // Show appropriate message based on trip duration
    if (tripDuration > 7) {
      alert('Your Pack-It is being created! Since your trip is over 7 days, the system will implement deep research to provide you with comprehensive recommendations. This process will likely take 10 minutes or longer. You\'ll be notified when your Pack-It is ready.');
    } else {
      alert('Your Pack-It is being created! This will take a few moments. You\'ll be notified when your Pack-It is ready.');
    }

    setIsSubmitting(true);

    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${token}`,
    };
  
    const payload = {
      name: formData.tripName,
      description: formData.description,
      startDate: formData.startDate ? formData.startDate.toISOString() : null,
      endDate: formData.endDate ? formData.endDate.toISOString() : null,
      tripType: formData.tripType,
      isFlexibleDate: formData.isFlexibleDate,
      includesKids: formData.includesKids,
      includesPets: formData.includesPets,
      alertFrequency: formData.alertFrequency,
      packingNeeds: formData.packingNeeds,
      interests: formData.interests,
      checkpoints: destinations.map((destination) => ({
        checkpointType: 'destination',
        name: destination.name,
        minimumNumberOfDesiredDaysAtLocation: destination.minimumNumberOfDesiredDaysAtLocation,
        isStart: destination.isStart,   // pass this along
        isEnd: destination.isEnd        // pass this along
      })),
      colorCategory: formData.selectedColors[0] || null, // Assume one color for simplicity
      primaryTravelMode: formData.primaryTravelMode || null,
      secondaryTravelModes: formData.secondaryTravelModes || [],
      ClientID: connectionId
    };
   
    try {
      const response = await axios.post(`${apiUrl}/briefings`, payload, { headers });
  
      if (response.status === 201 || response.status === 200) {
        navigate('/pack-it'); // Navigate back to the Pack-It list
      } else {
        throw new Error('Failed to create Pack-It');
      }
    } catch (error) {
      console.error('Error creating Pack-It:', error);
      alert('An error occurred while creating your Pack-It. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-packit-container">
      <h1>Create Your Pack-It</h1>
      <form className="create-packit-form" onSubmit={handleSubmit}>
        {selectedNotesSummary && selectedNotesSummary.length > 0 && (
          <div className="form-section">
            <h3 className="section-title">Selected Notes</h3>
            <div className="selected-notes-summary">
              {selectedNotesSummary.map((note) => (
                <div key={note.noteID} className="selected-note-summary-card">
                  <div className="selected-note-summary-header">
                    <span className="selected-note-summary-title">{note.title}</span>
                    {note.category && (
                      <span className="selected-note-summary-category">{note.category}</span>
                    )}
                  </div>
                  {note.snippet && (
                    <p className="selected-note-summary-snippet">{note.snippet}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Basic Information Section */}
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>
          
          <label>
            Trip Name
            <input
              type="text"
              name="tripName"
              value={formData.tripName}
              onChange={handleChange}
              placeholder="Enter trip name"
              required
            />
          </label>
          
          <label>
            Description
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter trip description"
              rows="3"
            ></textarea>
          </label>
          
          <div className="horizontal-group">
            <div>
              <label>Start Date</label>
              <DatePicker
                selected={formData.startDate}
                onChange={(date) => {
                  setFormData(prev => {
                    const newData = { ...prev, startDate: date };
                    // Clear end date if it's before the new start date
                    if (date && prev.endDate && prev.endDate < date) {
                      newData.endDate = null;
                    }
                    return newData;
                  });
                }}
                placeholderText="Select start date"
                required
                popperPlacement="top-start"
                showFixedNumberOfWeeks={true}
                fixedHeight={true}
              />
            </div>
            <div>
              <label>End Date</label>
              <DatePicker
                selected={formData.endDate}
                onChange={(date) => setFormData(prev => ({ ...prev, endDate: date }))}
                placeholderText="Select end date"
                required
                minDate={formData.startDate}
                popperPlacement="top-start"
                showFixedNumberOfWeeks={true}
                fixedHeight={true}
                openToDate={formData.startDate || new Date()}
              />
            </div>
          </div>
          
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isFlexibleDate"
                checked={formData.isFlexibleDate}
                onChange={handleChange}
              />
              <span>Are these dates flexible?</span>
            </label>
          </div>
        </div>

        {/* Color Categories Section */}
        <div className="form-section">
          <h3 className="section-title">Color Categories</h3>
          <div className="icon-selection">
            <div className="options">
                             {Object.entries(colorCategories).map(([color, description]) => {
                 const normalizedColor = color.toLowerCase().replace(/\s+/g, '-');
                 
                                   // Color mapping for different categories - using the exact same approach as the original form
                  const colorMap = {
                    'gatherings': 'rgb(87,129,252)', // blue
                    'food': 'rgb(252,99,85)', // red
                    'forests': 'rgb(0,225,60)', // green
                    'dragons': 'rgb(126,85,252)', // purple
                    'outdoors': 'rgb(255,79,158)', // pink
                    'wildlife': 'rgb(252,243,87)', // yellow
                    'water': 'rgb(255,153,0)', // orange
                    'blue': 'rgb(87,129,252)',
                    'red': 'rgb(252,99,85)',
                    'green': 'rgb(0,225,60)',
                    'purple': 'rgb(126,85,252)',
                    'pink': 'rgb(255,79,158)',
                    'yellow': 'rgb(252,243,87)',
                    'orange': 'rgb(255,153,0)',
                    'light-blue': 'rgb(85,215,215)',
                    'ltblue': 'rgb(85,215,215)',
                    'Ltblue': 'rgb(85,215,215)'
                  };

                  const colorRGB = colorMap[normalizedColor] || colorMap[color.toLowerCase()] || '#ccc';

                  return (
                    <button
                      type="button"
                      key={color}
                      className={`icon-button ${
                        formData.selectedColors.includes(color) ? 'selected' : ''
                      }`}
                      onClick={() => handleColorToggle(color)}
                    >
                      <span
                        className={`color-indicator ${normalizedColor}`}
                        style={{ backgroundColor: colorRGB }}
                      ></span>
                      <span className="button-text">{description}</span>
                    </button>
                  );
              })}
            </div>
          </div>
        </div>

        {/* Trip Type Section */}
        <div className="form-section">
          <h3 className="section-title">Trip Type</h3>
          <div className="icon-selection">
            <div className="options">
              {['Solo', 'Couples', 'Friends', 'Family'].map((type) => (
                <button
                  type="button"
                  key={type}
                  className={`icon-button ${formData.tripType === type.toLowerCase() ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, tripType: type.toLowerCase() }))}
                >
                  <img src={`/images/${type.toLowerCase()}.jpeg`} alt={type} />
                  <span className="button-text">{type}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
                {/* Packing Needs Section */}
        <div className="form-section">
          <h3 className="section-title">Packing Needs</h3>
          <div className="icon-selection">
            <div className="options">
              {['Lightweight', 'Formal', 'Snow', 'Hiking', 'Beach', 'Camping'].map((category) => (
                <button
                  type="button"
                  key={category}
                  className={`icon-button ${formData.packingNeeds.includes(category.toLowerCase()) ? 'selected' : ''}`}
                  onClick={() => handlePackingNeedsToggle(category.toLowerCase())}
                >
                  <img src={`/images/${category.toLowerCase()}.jpeg`} alt={category} />
                  <span className="button-text">{category}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Interests Section */}
        <div className="form-section">
          <h3 className="section-title">Interests</h3>
          <div className="bubbles-container">
            {allInterests.map((interest, index) => (
              <button
                type="button"
                key={index}
                className={`bubble ${
                  formData.interests.includes(interest) ? 'selected' : ''
                }`}
                onClick={() => handleInterestToggle(interest)}
              >
                {interest}
              </button>
            ))}
            <button
              type="button"
              className="add-bubble-button"
              onClick={() => setShowAddBubbleModal(true)}
            >
              +
            </button>
          </div>
          
          {showAddBubbleModal && (
            <div className="modal">
              <div className="modal-content">
                <h4>Add New Interest</h4>
                <input
                  type="text"
                  value={customInterest}
                  onChange={(e) => setCustomInterest(e.target.value)}
                  placeholder="Enter new interest"
                />
                <div className="modal-buttons">
                  <button onClick={handleAddCustomInterest}>Add</button>
                  <button onClick={() => setShowAddBubbleModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Trip Details Section */}
        <div className="form-section">
          <h3 className="section-title">Trip Details</h3>
          
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="includesKids"
                checked={formData.includesKids}
                onChange={handleChange}
              />
              <span>Does this trip include kids?</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="includesPets"
                checked={formData.includesPets}
                onChange={handleChange}
              />
              <span>Does this trip include pets?</span>
            </label>
          </div>
        </div>

        {/* Travel Modes Section */}
        <div className="form-section">
          <h3 className="section-title">Travel Modes</h3>
          <p className="section-description">
            Select your primary mode of travel to reach your destination, and any secondary modes you'll use during your trip.
          </p>
          
          <div className="travel-modes-container">
            <div className="primary-travel-mode">
              <h4 className="subsection-title">Primary Mode of Travel</h4>
              <p className="subsection-description">How will you primarily get to your destination?</p>
              <div className="travel-mode-options">
                {[
                  { value: 'plane', label: '✈️ Flight' },
                  { value: 'driving', label: '🚗 Driving' },
                  { value: 'train', label: '🚂 Train' },
                  { value: 'bus', label: '🚌 Bus' },
                  { value: 'ferry', label: '🚢 Ferry' },
                  { value: 'walking', label: '🚶 Walking' },
                  { value: 'public_transit', label: '🚇 Public Transit' },
                  { value: 'rideshare', label: '🚕 Rides' }
                ].map((mode) => (
                  <button
                    type="button"
                    key={mode.value}
                    className={`travel-mode-button ${formData.primaryTravelMode === mode.value ? 'selected' : ''}`}
                    onClick={() => handlePrimaryTravelModeChange(mode.value)}
                  >
                    <span className="travel-mode-icon">{mode.label.split(' ')[0]}</span>
                    <div className="travel-mode-details">
                      <span className="travel-mode-label">{mode.label.replace(/^[^\s]+\s/, '')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="secondary-travel-modes">
              <h4 className="subsection-title">Secondary Modes of Travel</h4>
              <p className="subsection-description">What other transportation will you use during your trip?</p>
              <div className="travel-mode-options">
                {[
                  { value: 'plane', label: '✈️ Flight' },
                  { value: 'driving', label: '🚗 Driving' },
                  { value: 'train', label: '🚂 Train' },
                  { value: 'bus', label: '🚌 Bus' },
                  { value: 'ferry', label: '🚢 Ferry' },
                  { value: 'walking', label: '🚶 Walking' },
                  { value: 'public_transit', label: '🚇 Public Transit' },
                  { value: 'rideshare', label: '🚕 Rides' }
                ].map((mode) => (
                  <button
                    type="button"
                    key={mode.value}
                    className={`travel-mode-button secondary ${formData.secondaryTravelModes && formData.secondaryTravelModes.includes(mode.value) ? 'selected' : ''}`}
                    onClick={() => handleSecondaryTravelModeToggle(mode.value)}
                  >
                    <span className="travel-mode-icon">{mode.label.split(' ')[0]}</span>
                    <div className="travel-mode-details">
                      <span className="travel-mode-label">{mode.label.replace(/^[^\s]+\s/, '')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Destinations Section */}
        <div className="form-section">
          <h3 className="section-title">Destinations</h3>
          <p className="section-description">
            You can add additional cities or addresses to build your itinerary and trip while specifying the desired number of days at each location.
          </p>
          
          <div className="destinations-container">
            {destinations.map((destination) => (
              <div key={destination.id} className="destination-row">
                <div className="destination-inputs">
                  <input
                    type="text"
                    placeholder="Enter destination"
                    value={destination.name}
                    onChange={(e) =>
                      handleUpdateDestination(destination.id, 'name', e.target.value)
                    }
                    className="destination-name-input"
                  />
                  <label className="checkbox-label"><span>Days to visit:</span></label>
                  <input
                    type="number"
                    min="0"
                    value={destination.minimumNumberOfDesiredDaysAtLocation}
                    onChange={(e) =>
                      handleUpdateDestination(destination.id, 'minimumNumberOfDesiredDaysAtLocation', e.target.value)
                    }
                    className="destination-days-input"
                    placeholder="Days"
                  />
                </div>
                
                <div className="destination-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={destination.isStart}
                      onChange={(e) =>
                        handleUpdateDestination(destination.id, 'isStart', e.target.checked)
                      }
                    />
                    <span>Is Start?</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={destination.isEnd}
                      onChange={(e) =>
                        handleUpdateDestination(destination.id, 'isEnd', e.target.checked)
                      }
                    />
                    <span>Is End?</span>
                  </label>
                </div>
                
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => handleRemoveDestination(destination.id)}
                >
                  Remove
                </button>
              </div>
            ))}
            
            <button
              type="button"
              className="add-destination-button"
              onClick={handleAddDestination}
            >
               Add Destination
            </button>
          </div>
        </div>

        <div className="button-group">
        <button
            type="button"
            className="create-note-button cancel"
            onClick={onBack}
        >
            Back
        </button>
        <button 
            type="submit" 
            className="create-note-button"
            disabled={isSubmitting}
        >
            {isSubmitting ? 'Creating...' : 'Create Pack-It'}
        </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePackitForm;
